import { Box, Center, HStack, Icon, Text } from '@chakra-ui/react';
import type { SessionOperate } from '@halo/chat-sdk';
import { StateEnum } from '@halo/chat-sdk';
import {
  GroupIcon,
  SessionMutedIcon,
  RobotIcon,
  OfficialIcon,
  Tooltip,
} from '@halo/ui-chat-react';
import { useTranslation } from 'react-i18next';

interface ChatMetaHeaderProps {
  sessionName: string;
  name?: string;
  state?: StateEnum;
  isGroup: boolean;
  isCollapseChat?: boolean;
  updateAtDisplay: string | undefined;
  operateAccess?: Record<keyof typeof SessionOperate, boolean>;
  isRobot?: boolean;
  tooltip?: string;
  official?: boolean;
}

export function ChatMetaHeader({
  sessionName,
  state,
  isGroup,
  isCollapseChat,
  updateAtDisplay,
  operateAccess,
  isRobot,
  tooltip,
  official,
}: ChatMetaHeaderProps) {
  const { t } = useTranslation();
  const chatName = useMemo(() => {
    if (isGroup || isCollapseChat) {
      return isCollapseChat ? t('chat.collapsedChatText') : sessionName;
    }
    return state === StateEnum.DELETE_ACCOUNT ? t('chat.deleteAccountText') : sessionName;
  }, [isGroup, isCollapseChat, state, sessionName, t]);

  return (
    <HStack
      className="ChatMetaHeader"
      justify="space-between"
      align="center"
      gap="1"
      fontSize="sm"
    >
      {isGroup && !isCollapseChat && (
        <Icon>
          <GroupIcon />
        </Icon>
      )}
      {isRobot && !isCollapseChat && (
        <Icon w="1em" h="1em">
          <RobotIcon />
        </Icon>
      )}
      <Box flex="1" overflow="hidden" lineHeight="1">
        <Text fontSize="1em" fontWeight="semibold" lineClamp="1" lineHeight="1.5">
          {chatName}
          {isRobot && official && !isCollapseChat && (
            <Tooltip
              showArrow={true}
              content={tooltip}
              openDelay={0}
              closeDelay={0}
              positioning={{
                placement: 'top',
              }}
            >
              <OfficialIcon width="1em" height="1em" />
            </Tooltip>
          )}
        </Text>
      </Box>
      {operateAccess?.MUTED && (
        <Center>
          <SessionMutedIcon />
        </Center>
      )}
      <Text fontSize="0.857em" fontWeight="medium" color="fg.muted">
        {updateAtDisplay}
      </Text>
    </HStack>
  );
}
